import React, { forwardRef, ReactElement, CSSProperties, useContext } from 'react';
import { DOMProps, HoverEvents } from '@react-types/shared';
import { useHover, mergeProps, usePress } from 'react-aria';

import { TestProps } from '../../../internal/types/testTypes';
import { cn } from '../../../lib/css/className/className';
import './Row.css';
import { ColumnsWidthContext, InnerBordersContext } from '../context';
import { TableCellProps } from '../Cell/Cell';

const cls = cn('TableRow');

type CellWidths = {
    width: React.CSSProperties['width'];
    minWidth: React.CSSProperties['minWidth'];
    maxWidth: React.CSSProperties['maxWidth'];
};

/**
 * Функция для определения ширин ячеек, которая учитывает, что часть ячеек может объединяться через colSpan
 * @param colSpans массив, длина которого соответствует реальному количеству ячеек.
 * colSpans[i] - количество ячеек, которое объединяет внутри себя i-ая ячейка
 * @param originalWidths - оригинальный массив длин, без учета ячеек, которые объединяют внутри себя другие ячейки
 * @returns массив длин для реальных ячеек с учетом colSpan
 */
export const getCellWidths = (colSpans: number[], originalWidths?: string[]): (CellWidths | undefined)[] => {
    let curWidthIndex = 0;
    const widths: (CellWidths | undefined)[] = [];

    // итерируемся по реальным ячейкам
    for (let cellIndex = 0; cellIndex < colSpans.length; cellIndex++) {
        const startIndex = curWidthIndex;
        // для каждой ячейки(которая с учетом colSpan внутри содержит N ячеек)
        // получаем массив входящих длин оригинальных ячеек
        const widthsOfCell = originalWidths?.slice(startIndex, startIndex + (colSpans[cellIndex] ?? 1));
        const isAuto = widthsOfCell?.includes('auto');
        // суммируем длины через calc, исключаем auto, тк calc не поддерживает auto
        const sumString = originalWidths
            ?.slice(startIndex, startIndex + (colSpans[cellIndex] ?? 1))
            .filter(width => width !== 'auto')
            .join(' + ');
        const width = sumString ? `calc(${sumString})` : undefined;

        if (width) {
            // если внутри было auto, то делаем длину auto, но не меньше чем сумма остальных длин ячейки
            widths.push({
                width: isAuto ? 'auto' : width,
                minWidth: width,
                maxWidth: isAuto ? 'auto' : width,
            });
            // когда внутри ячейки было только auto
        } else if (isAuto) {
            widths.push({
                width: 'auto',
                minWidth: 'auto',
                maxWidth: 'auto',
            });
            // когда не передали массив оригинальных длин
        } else {
            widths.push(undefined);
        }

        curWidthIndex += colSpans[cellIndex] ?? 0;
    }

    return widths;
};

export interface TableRowProps extends TestProps, DOMProps, HoverEvents {
    /**
     * Дочерние элементы в виде TableCell. Никакие другие элементы не должны находиться внутри этого компонента
     * @example <TableRow><TableCell>{Some content}</TableCell></TableRow>
     * @expectedNode `TableCell`
     */
    children?: ReactElement<TableCellProps> | ReactElement<TableCellProps>[];

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Признак выделения строки
     */
    selected?: boolean;

    /**
     * Включение обработчика наведения
     */
    hoverable?: boolean;

    /**
     * Отключает верхний разделитель
     */
    withoutTopBorder?: boolean;

    /**
     * Инлайновые стили строки
     */
    style?: CSSProperties;

    /**
     * Обработчик нажатия на строку
     */
    onClick?: () => void;

    /**
     * Отображает справочную информацию при наведении на строку
     */
    title?: string;

    /**
     * Флаг наличия ошибки в строке (используется для подскролла к заданной строке)
     */
    'data-form-error'?: boolean;
}

/**
 * Компонент для добавления строки в таблице
 */
export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
    {
        style,
        title,
        selected,
        hoverable,
        children,
        className,
        withoutTopBorder,
        'data-testid': dataTestId = 'TableRow',
        onClick,
        onHoverEnd,
        onHoverStart,
        onHoverChange,
        ...props
    },
    ref,
) {
    const columnsWidthArray = useContext(ColumnsWidthContext);
    const innerBorders = useContext(InnerBordersContext);

    const { isHovered, hoverProps } = useHover({
        isDisabled: !hoverable,
        onHoverEnd,
        onHoverStart,
        onHoverChange,
    });
    const { pressProps } = usePress({
        onPress: onClick,
    });

    const colSpans =
        React.Children.map(children, child => {
            return child?.props.colspan ?? 1;
        }) ?? [];

    const widths = getCellWidths(colSpans, columnsWidthArray);

    return (
        <tr
            title={title}
            ref={ref}
            style={style}
            {...mergeProps(hoverProps, onClick ? pressProps : {})}
            className={cls(
                '',
                {
                    selected,
                    hovered: isHovered,
                    withoutTopBorder: withoutTopBorder || innerBorders === 'none',
                },
                [className],
            )}
            data-testid={dataTestId}
            data-form-error={props['data-form-error']}
        >
            {React.Children.map(children, (child, index) => {
                if (columnsWidthArray && columnsWidthArray?.length > 0 && child) {
                    return React.cloneElement(child, {
                        ...child.props,
                        style: {
                            ...child.props.style,
                            ...widths[index],
                        },
                    });
                }

                return child;
            })}
        </tr>
    );
});
